import UserService from "services/User";

/**
 * Check if a user is considered active.
 * @param {Object} user - User object with status field
 * @returns {boolean} True if user is active
 */
const isUserActive = user => user.status === "active" || !user.status;

/**
 * Format user name with deactivated indicator if applicable.
 * @param {string} fullName - User's full name
 * @param {string} status - User's status
 * @returns {string} Formatted name with "(Deactivated)" suffix if deactivated
 */
const formatUserName = (fullName, status) =>
  status === "deactivated" ? `${fullName} (Deactivated)` : fullName;

/**
 * Format ALL users for filter dropdowns.
 * Shows all users including deactivated, with visual indicator.
 * Use this for filter contexts where you need to filter by any user.
 *
 * @param {Array} users - Array of user objects with id, full_name, status
 * @param {boolean} options.includeNone - Whether to prepend a "None" option
 * @returns {Array} Array of dropdown options { id, name } or { key, value, text }
 */
const formatUsersForFilter = users => {
  const options = users.map(({ id, full_name, name, status }) => ({
    id,
    name: formatUserName(full_name || name, status),
  }));

  return options;
};

/**
 * Filter users for ASSIGNMENT dropdowns in forms.
 * Shows active users + currently selected values (even if deactivated).
 * Deactivated users are labeled with "(Deactivated)" suffix.
 *
 * Use this for assignment contexts where you shouldn't allow selecting
 * newly deactivated users, but need to show currently assigned ones.
 *
 * @param {Array} users - Array of user objects with id, full_name, status
 * @param {Array} selectedIds - Array of currently selected user IDs
 * @returns {Array} Filtered array of dropdown options { key, value, text }
 */
const filterUsersForDropdown = (users, selectedIds = []) => {
  return users
    .filter(user => {
      const isCurrentlySelected = selectedIds.includes(user.id);
      return isUserActive(user) || isCurrentlySelected;
    })
    .map(({ id, full_name, status }) => ({
      key: id,
      value: id,
      text: formatUserName(full_name, status),
    }));
};

/**
 * Query function wrapper for RuvixxSelect user filters.
 * Fetches matching users and formats names with deactivated indicator.
 * Use this as queryFn in filter configs.
 *
 * The raw user is spread through so RuvixxSelect's option mapper still sees
 * `deleted` and can disable those options.
 *
 * @param {Object} filters - Optional filters to pass to UserService
 * @returns {Promise<Array>} Array of user objects with a formatted `name`
 */
const getUsersForFiltersQuery = async (filters = {}) => {
  const rawUsers = await UserService.getUsersForFilters(filters);
  return rawUsers.map(user => ({
    ...user,
    name: formatUserName(user.name || user.full_name, user.status),
  }));
};

export {
  filterUsersForDropdown,
  formatUsersForFilter,
  getUsersForFiltersQuery,
  isUserActive,
  formatUserName,
};
